import { LockIcon, UnlockIcon } from '@/components/icons/Icon';
import CancelButton from '@/components/buttons/CancelButton';
import { DialogBody, DialogCloseTrigger, DialogContent, DialogFooter, DialogHeader, DialogRoot } from '@/components/ui/dialog';
import { Field } from '@/components/ui/field';
import { InputGroup } from '@/components/ui/input-group';
import { PasswordInput } from '@/components/ui/password-input';
import { Fieldset, Group, Heading, Stack } from '@chakra-ui/react';
import PasswordStrengthIndicator from '@/components/PasswordStrengthIndicator';
import SubmitButton from '@/components/buttons/SubmitButton';
import { authService } from '@/service/AuthService';
import { useEffect, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';

interface ChangePasswordEntry {
    oldPassword: string;
    newPassword: string;
    confirmNewPassword: string;
}

interface ChangePasswordDialogProps {
  isOpen: boolean;
  isSubmitting: boolean;
  setIsSubmitting: (loading: boolean) => void;
  onClose: () => void;
}

const ChangePasswordDialog = ({
  isOpen,
  isSubmitting,
  setIsSubmitting,
  onClose,
}: ChangePasswordDialogProps) => {
  const {
    register,
    handleSubmit,
    watch,
    reset,
    formState: { errors },
  } = useForm<ChangePasswordEntry>({
    defaultValues: {
      oldPassword: '',
      newPassword: '',
      confirmNewPassword: '',
    },
  });
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    if (isOpen) {
      reset({
        oldPassword: '',
        newPassword: '',
        confirmNewPassword: '',
      });
      setErrorMessage('');
    }
  }, [isOpen, reset]);

  const handleClose = () => {
    reset({
      oldPassword: '',
      newPassword: '',
      confirmNewPassword: '',
    });
    setErrorMessage('');
    onClose();
  };

  const onSubmitPasswordChange: SubmitHandler<ChangePasswordEntry> = async (entry) => {
    console.info('changing password...');
    try {
      setIsSubmitting(true);
      setErrorMessage('');
      await authService.passwordChange(entry.oldPassword, entry.newPassword);
      handleClose();
      console.info(`password changed...`);
    } catch (error) {
      console.error(error);
      const message = error instanceof Error ? error.message?.trim() : '';
      setErrorMessage(message || 'Fehler');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <DialogRoot open={isOpen} onOpenChange={() => handleClose()} size='sm' placement='center' motionPreset='scale'>
      <DialogContent>
        <form noValidate onSubmit={handleSubmit(onSubmitPasswordChange)}>
          <DialogHeader>
            <Heading textAlign='center' size='md'>Passwort ändern 🔐</Heading>
          </DialogHeader>
          <DialogCloseTrigger />
          <DialogBody>
            <Fieldset.Root invalid={!!errorMessage}>
              <Fieldset.ErrorText>{errorMessage}</Fieldset.ErrorText>
              <Stack gap={4}>
                <Field id="oldPassword" label='Aktuelles Passwort' required invalid={!!errors.oldPassword} errorText={errors.oldPassword?.message}>
                  <InputGroup w='full' startElement={<UnlockIcon color='gray.400' />}>
                    <PasswordInput
                      {...register('oldPassword', {
                        required: 'Aktuelles Passwort ist erforderlich',
                      })}
                    />
                  </InputGroup>
                </Field>
                <Field id="newPassword" label='Neues Passwort' required invalid={!!errors.newPassword} errorText={errors.newPassword?.message}>
                  <InputGroup w='full' startElement={<LockIcon color='gray.400' />}>
                    <PasswordInput
                      {...register('newPassword', {
                        required: 'Neues Passwort ist erforderlich',
                      })}
                    />
                  </InputGroup>
                </Field>
                <PasswordStrengthIndicator password={watch('newPassword')} shortScoreWord='zu kurz'
                  scoreWords={['schwach', 'schwach', 'okay', 'gut', 'stark']} />
                <Field id="confirmNewPassword" label='Neues Passwort wiederholen' required invalid={!!errors.confirmNewPassword} errorText={errors.confirmNewPassword?.message}>
                  <InputGroup w='full' startElement={<LockIcon color='gray.400' />}>
                    <PasswordInput
                      {...register('confirmNewPassword', {
                        required: 'Bitte Passwort wiederholen',
                        validate: (value) => value === watch('newPassword') || 'Die beiden Passwörter sind nicht identisch.',
                      })}
                    />
                  </InputGroup>
                </Field>
              </Stack>
            </Fieldset.Root>
          </DialogBody>
          <DialogFooter>
            <Group gap={2} flexWrap="wrap" justifyContent="flex-end" w="full">
              <CancelButton onClick={handleClose} />
              <SubmitButton caption='Passwort ändern' isSubmitting={isSubmitting} />
            </Group>
          </DialogFooter>
        </form>
      </DialogContent>
    </DialogRoot>
  );
};

export default ChangePasswordDialog;
